import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { RepairDocketStore } from './repair-docket.store';
import { CustomerService } from '../shared';
import { ConfigService } from '../shared/services/config.service';
import {
  RetailItemStoreService,
  SALE_ITEM_SERVICE
} from '../shared/retail-item-list/retail-item.store.service';
import { AbstractPaymentService } from '../shared/services/abstract-payment.service';
import {
  filter,
  map,
  mergeMap,
  startWith,
  take,
  takeUntil
} from 'rxjs/operators';
import { base64ToBlob } from '../shared/helpers/base64ToBlob';
import { Subject, timer as observableTimer } from 'rxjs';
import { DOCUMENT, NgIf, AsyncPipe } from '@angular/common';
import { WorkshopJobRetailItemSaleService } from '../shared/services/workshop-job-retail-item-sale.service';
import { RepairDocketPaymentService } from '../shared/services/repair-docket-payment.service';
import { RepairDocketService } from '../shared/services/repair-docket.service';
import { WorkshopJobListStore } from './workshop-jobs-list/workshop-job-list.store.service';
import { WorkshopJobService } from '../shared/services/workshop-job.service';
import { REPAIR_DOCKET } from '../../testIds';
import { CustomerAddressPipe } from '../shared/pipes/customer-address.pipe';
import { CustomerContactDetailsPipe } from '../shared/pipes/customer-contact-details.pipe';
import { CustomerNamePipe } from '../shared/pipes/customer-name.pipe';
import { PriceContainerV2Component } from '../shared/price-container-v2/price-container-v2.component';
import { WorkshopJobsListComponent } from './workshop-jobs-list/workshop-jobs-list.component';
import { RetailDocketSettingsComponent } from './repair-docket-settings/retail-docket-settings.component';
import { RepairDocketInfoComponent } from './repair-docket-info/repair-docket-info.component';
import { DocumentContactComponent } from '../shared/contact-management/document-contact/document-contact.component';
import { RouterLink } from '@angular/router';
import { TestIdDirective } from '../shared/directives/test-id.directive';
import { MatTooltip } from '@angular/material/tooltip';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatCard, MatCardContent } from '@angular/material/card';
import { ContactService } from '../shared/services/contact.service';

@Component({
  selector: 'app-repair-docket',
  templateUrl: './repair-docket.component.html',
  providers: [
    RepairDocketStore,
    RepairDocketService,
    CustomerService,
    WorkshopJobListStore,
    WorkshopJobService,
    WorkshopJobRetailItemSaleService,
    { provide: SALE_ITEM_SERVICE, useClass: WorkshopJobRetailItemSaleService },
    RetailItemStoreService,
    { provide: AbstractPaymentService, useClass: RepairDocketPaymentService },
    ContactService
  ],
  standalone: true,
  imports: [
    MatCard,
    MatCardContent,
    NgIf,
    NgxSkeletonLoaderModule,
    MatTooltip,
    TestIdDirective,
    RouterLink,
    DocumentContactComponent,
    RepairDocketInfoComponent,
    RetailDocketSettingsComponent,
    WorkshopJobsListComponent,
    PriceContainerV2Component,
    AsyncPipe,
    CustomerNamePipe,
    CustomerContactDetailsPipe,
    CustomerAddressPipe
  ]
})
export class RepairDocketComponent implements OnDestroy {
  testIds = REPAIR_DOCKET;
  customer$ = this.repairDocketStore.customer$;
  customerLoading$ = this.repairDocketStore.customerLoading$;
  docketLoading$ = this.repairDocketStore.docketLoading$;
  docket$ = this.repairDocketStore.docket$;
  docketId$ = this.docket$.pipe(
    map((docket) => docket?.id),
    filter(Boolean)
  );

  finalized$ = this.docket$.pipe(
    map((docket) => Boolean(docket?.finalized_at))
  );
  paymentContainerCharges$ =
    this.repairDocketStore.paymentContainerItemCharges$;
  dateTimeFormat = ConfigService.dates.longDateTimeFormat;
  @ViewChild('printFrame') printFrame;
  private ngUnsubscribe = new Subject<void>();
  docketCurrency$ = this.docket$.pipe(
    filter(Boolean),
    map((docket) => docket.currency_code),
    startWith('GBP')
  );

  constructor(
    private repairDocketStore: RepairDocketStore,
    private repairDocketService: RepairDocketService,
    private configService: ConfigService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  checkDocketStatus() {
    this.repairDocketStore.checkDocketStatus();
  }

  updateDocketContact(contactId: number | null) {
    this.repairDocketStore.docket$
      .pipe(
        take(1),
        takeUntil(this.ngUnsubscribe),
        mergeMap((docket) =>
          this.repairDocketService.updateRepairDocket(docket.id, {
            contact_id: contactId
          })
        )
      )
      .subscribe();
  }

  print(download = false) {
    this.repairDocketStore.docket$
      .pipe(
        take(1),
        takeUntil(this.ngUnsubscribe),
        mergeMap((docket) =>
          this.repairDocketService
            .print(docket.id)
            .pipe(map((response) => ({ docket, response })))
        )
      )
      .subscribe(({ docket, response: base64Pdf }) => {
        const src = URL.createObjectURL(
          base64ToBlob(base64Pdf, 'application/pdf')
        );
        if (download) {
          const link = this.document.createElement('a') as HTMLAnchorElement;
          link.href = src;
          const ref = docket.account_reference
            ? `Sales Invoice ${docket.account_reference}`
            : `Draft Sales Invoice ${docket.id}`;
          link.download = `${ref}.pdf`;
          link.click();
        } else {
          this.printFrame.nativeElement.src = src;
          this.printIframe();
        }
      });
  }

  printIframe() {
    observableTimer(500)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.printFrame.nativeElement.contentWindow.print();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
